import { isRecord } from '@utils/json'

import { safeLocalStorage } from '@hooks/useStorage'

import type { WebSocketMessage } from './types'

const MESSAGE_STORE_STORAGE_KEY = 'tmca:websocket:handled-message-ids'
const MESSAGE_STORE_CHANNEL_NAME = 'tmca:websocket:message-store'
const DEFAULT_MAX_MESSAGE_IDS = 1000

type MessageStoreBroadcastMessage = { type: 'handled-message-id'; Id: string }

type RememberMessageOptions = { broadcast?: boolean; persist?: boolean }

type CreateHandledMessageStoreOptions = { storageKey?: string; maxIds?: number; persist?: boolean }

export type HandledMessageStore = {
  rememberMessage: (message: WebSocketMessage) => boolean
}

const readStoredIds = (storageKey: string): string[] => {
  const storedIds = safeLocalStorage.getJSON<unknown>(storageKey)
  if (!Array.isArray(storedIds)) return []

  return storedIds.filter((value): value is string => typeof value === 'string')
}

const writeStoredIds = (storageKey: string, ids: string[]): void => {
  safeLocalStorage.setJSON(storageKey, ids)
}

const isMessageStoreBroadcastMessage = (value: unknown): value is MessageStoreBroadcastMessage => {
  if (!isRecord(value)) return false

  return value.type === 'handled-message-id' && typeof value.Id === 'string'
}

export const createHandledMessageStore = (
  options: CreateHandledMessageStoreOptions = {}
): HandledMessageStore => {
  const {
    storageKey = MESSAGE_STORE_STORAGE_KEY,
    maxIds = DEFAULT_MAX_MESSAGE_IDS,
    persist = true,
  } = options
  const ids = new Set<string>()
  let order: string[] = []
  const channel =
    persist && typeof window !== 'undefined' && typeof window.BroadcastChannel !== 'undefined'
      ? new window.BroadcastChannel(MESSAGE_STORE_CHANNEL_NAME)
      : null

  const addId = (id: string): void => {
    ids.add(id)
    order.push(id)

    while (order.length > maxIds) {
      const removedId = order.shift()
      if (removedId) ids.delete(removedId)
    }
  }

  const replaceIds = (nextIds: string[]): void => {
    const cappedIds = nextIds.slice(-maxIds)
    ids.clear()
    order = []

    cappedIds.forEach((id) => {
      if (!ids.has(id)) {
        ids.add(id)
        order.push(id)
      }
    })
  }

  const remember = (id: string, options?: RememberMessageOptions): boolean => {
    if (ids.has(id)) return false

    addId(id)

    if (persist && options?.persist !== false) writeStoredIds(storageKey, order)

    if (options?.broadcast !== false) {
      channel?.postMessage({ type: 'handled-message-id', Id: id })
    }

    return true
  }

  const rememberMessage = (message: WebSocketMessage): boolean => {
    const messageId = extractMessageId(message)

    if (!messageId) return true
    return remember(messageId)
  }

  const handleBroadcastMessage = (event: MessageEvent<unknown>): void => {
    const message = event.data

    if (!isMessageStoreBroadcastMessage(message)) return

    remember(message.Id, { broadcast: false, persist: false })
  }

  const handleStorageEvent = (event: StorageEvent): void => {
    if (event.key !== storageKey) return

    replaceIds(readStoredIds(storageKey))
  }

  if (persist) {
    replaceIds(readStoredIds(storageKey))
    channel?.addEventListener('message', handleBroadcastMessage)

    if (typeof window !== 'undefined') window.addEventListener('storage', handleStorageEvent)
  }

  return {
    rememberMessage,
  }
}

let handledMessageStore: HandledMessageStore | null = null

export const getHandledMessageStore = (): HandledMessageStore => {
  if (!handledMessageStore) handledMessageStore = createHandledMessageStore()

  return handledMessageStore
}

const extractMessageId = (message: unknown): string | null => {
  if (!isRecord(message)) return null
  const value = message.Id

  if (typeof value === 'string' && value.trim()) return value
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)

  return null
}
